import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, SectionTitle } from "@/components/Kpi";
import { COLORS } from "@/components/Charts";
import { api } from "@/lib/api";
import { dateIt, eurShort, num } from "@/lib/format";

const REDD = [["totale", "Redditività totale"], ["non_garantito", "Redditività non garantito"],
  ["garantito", "Redditività garantito"]];

const VISTE = {
  caricato: {
    titolo: "Classifiche caricato",
    sub: "pratiche e importi caricati per agenzia, totali e per prodotto",
    campi: ["pezzi_caricato", "caricato",
      "pezzi_caricato_non_garantito", "caricato_non_garantito",
      "pezzi_caricato_garantito", "caricato_garantito",
      "pezzi_caricato_vivibanca", "caricato_vivibanca"],
  },
  liquidato: {
    titolo: "Classifiche liquidato",
    sub: "pratiche e importi liquidati per agenzia, totali e per prodotto",
    campi: ["pezzi_liquidato", "liquidato",
      "pezzi_liquidato_non_garantito", "liquidato_non_garantito",
      "pezzi_liquidato_garantito", "liquidato_garantito",
      "pezzi_liquidato_vivibanca", "liquidato_vivibanca"],
  },
};

const colore = (scarto) => {
  if (scarto === null || scarto === undefined) return "text-slate-300";
  return scarto >= 0 ? "text-emerald-600" : scarto >= -1 ? "text-amber-600" : "text-red-600";
};

/** Differenza in punti percentuali dal riferimento (media DBE o redditività richiesta). */
const scartoPunti = (v, rif) => (v === null || v === undefined || rif === null
  || rif === undefined ? null : v - rif);

const Cella = ({ v }) => (
  <td className="py-2 px-2 text-right tabular-nums text-slate-700">
    {v === null || v === undefined ? "—" : `${num(v, 2)}%`}
  </td>
);

/** Totale in nero con, tra parentesi, lo scostamento in punti dal riferimento. */
const CellaTotale = ({ v, riferimento }) => {
  const scarto = scartoPunti(v, riferimento);
  return (
    <td className="py-2 px-2 text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
      {v === null || v === undefined ? "—" : `${num(v, 2)}%`}
      {scarto === null ? null : (
        <span className={`ml-2 text-xs font-normal ${colore(scarto)}`}>
          ({scarto >= 0 ? "+" : ""}{num(scarto, 2)} pt)
        </span>
      )}
    </td>
  );
};

const linkAgenzia = (slug) => `/canale-corto/agenzie/${slug}`;

const TabellaRedditivita = ({ tipo, titolo, dati, mesi }) => {
  const { agenzie, media, target } = dati;
  return (
    <Card className="p-6" data-testid={`classifica-${tipo}`}>
      <div className="flex items-baseline justify-between gap-4 mb-4 flex-wrap">
        <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">{titolo}</div>
        {target ? (
          <div className="text-xs text-slate-500 font-mono">
            redditività richiesta {num(target, 2)}%
          </div>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 text-left">#</th>
              <th className="py-2 pr-3 text-left">Agenzia</th>
              {mesi.map((m) => <th key={m} className="py-2 px-2 text-right font-medium">{m}</th>)}
              <th className="py-2 px-2 text-right">Totale</th>
            </tr>
          </thead>
          <tbody>
            {agenzie.map((a, i) => (
              <tr key={a.agenzia} className="border-b border-slate-100 odd:bg-slate-50/60"
                data-testid={`classifica-${tipo}-riga-${i + 1}`}>
                <td className="py-2 pr-3 font-mono text-xs text-slate-400">{i + 1}</td>
                <td className="py-2 pr-3 font-medium whitespace-nowrap">
                  <Link to={linkAgenzia(a.agenzia.toLowerCase().replace(/ /g, "-"))}
                    className="hover:text-[#C8102E] transition-colors duration-200">{a.agenzia}</Link>
                </td>
                {a.valori.map((v, j) => <Cella key={j} v={v} />)}
                <CellaTotale v={a.totale} riferimento={target || (media && media.totale)} />
              </tr>
            ))}
            {media ? (
              <tr className="border-t-2 border-slate-300" data-testid={`classifica-${tipo}-media`}>
                <td className="py-2 pr-3" />
                <td className="py-2 pr-3 font-semibold whitespace-nowrap">{media.agenzia}</td>
                {media.valori.map((v, j) => <Cella key={j} v={v} />)}
                <CellaTotale v={media.totale} riferimento={target} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const TabellaValori = ({ blocco, mesi, campo }) => {
  const fmt = blocco.formato === "eur" ? (v) => eurShort(v) : (v) => num(v, 0);
  const tinta = campo.includes("liquidato") ? COLORS.liquidato : COLORS.caricato;
  const totale = blocco.totale.totale;
  return (
    <Card className="p-6" data-testid={`classifica-agenzie-${campo}`}>
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">
        {blocco.titolo} · classifica agenzie
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 text-left">#</th>
              <th className="py-2 pr-3 text-left">Agenzia</th>
              {mesi.map((m) => <th key={m} className="py-2 px-2 text-right font-medium">{m}</th>)}
              <th className="py-2 px-2 text-right">Totale (peso)</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {blocco.agenzie.map((a, i) => (
              <tr key={a.slug} className="border-b border-slate-100 odd:bg-slate-50/60"
                data-testid={`classifica-agenzie-${campo}-riga-${i + 1}`}>
                <td className="py-2 pr-3 text-xs text-slate-400">{i + 1}</td>
                <td className="py-2 pr-3 font-sans font-medium whitespace-nowrap">
                  <Link to={linkAgenzia(a.slug)}
                    className="hover:text-[#C8102E] transition-colors duration-200">{a.agenzia}</Link>
                </td>
                {a.valori.map((v, j) => (
                  <td key={j} className="py-2 px-2 text-right tabular-nums text-slate-600">
                    {v ? fmt(v) : "—"}
                  </td>
                ))}
                <td className="py-2 px-2 text-right font-semibold whitespace-nowrap">
                  {fmt(a.totale)}
                  {totale ? (
                    <span className="ml-2 text-xs font-normal" style={{ color: tinta }}
                      data-testid={`classifica-agenzie-${campo}-peso-${a.slug}`}>
                      ({num((a.totale / totale) * 100, 1)}%)
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 font-semibold"
              data-testid={`classifica-agenzie-${campo}-totale`}>
              <td className="py-2 pr-3" />
              <td className="py-2 pr-3 font-sans whitespace-nowrap">{blocco.totale.agenzia}</td>
              {blocco.totale.valori.map((v, j) => (
                <td key={j} className="py-2 px-2 text-right tabular-nums">{v ? fmt(v) : "—"}</td>
              ))}
              <td className="py-2 px-2 text-right whitespace-nowrap">
                {fmt(blocco.totale.totale)}
                <span className="ml-2 text-xs font-normal" style={{ color: tinta }}>(100,0%)</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const TabellaTassi = ({ campo, blocco, mesi }) => (
  <Card className="p-6" data-testid={`classifica-tassi-${campo}`}>
    <div className="flex items-baseline justify-between gap-4 mb-4 flex-wrap">
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
        {blocco.titolo} · classifica agenzie
      </div>
      <div className="text-xs text-slate-500 font-mono">
        media DBE {blocco.media_dbe === null ? "—" : `${num(blocco.media_dbe, 2)}%`}
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3 text-left">#</th>
            <th className="py-2 pr-3 text-left">Agenzia</th>
            {mesi.map((m) => <th key={m} className="py-2 px-2 text-right font-medium">{m}</th>)}
            <th className="py-2 px-2 text-right">Totale</th>
          </tr>
        </thead>
        <tbody>
          {blocco.agenzie.map((a, i) => (
            <tr key={a.slug} className="border-b border-slate-100 odd:bg-slate-50/60"
              data-testid={`classifica-tassi-${campo}-riga-${i + 1}`}>
              <td className="py-2 pr-3 font-mono text-xs text-slate-400">{i + 1}</td>
              <td className="py-2 pr-3 font-medium whitespace-nowrap">
                <Link to={linkAgenzia(a.slug)}
                  className="hover:text-[#C8102E] transition-colors duration-200">{a.agenzia}</Link>
              </td>
              {a.valori.map((v, j) => <Cella key={j} v={v} />)}
              <CellaTotale v={a.totale} riferimento={blocco.media_dbe} />
            </tr>
          ))}
          {blocco.media ? (
            <tr className="border-t-2 border-slate-300" data-testid={`classifica-tassi-${campo}-media`}>
              <td className="py-2 pr-3" />
              <td className="py-2 pr-3 font-semibold whitespace-nowrap">{blocco.media.agenzia}</td>
              {blocco.media.valori.map((v, j) => <Cella key={j} v={v} />)}
              <CellaTotale v={blocco.media.totale} riferimento={null} />
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  </Card>
);

export default function ClassificheCorto({ vista: vistaProp }) {
  const { vista: vistaParam } = useParams();
  const vista = vistaProp || vistaParam || "caricato";
  const [dati, setDati] = useState(null);
  const [prod, setProd] = useState(null);
  const [tassi, setTassi] = useState(null);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    setErrore("");
    if (vista === "tassi") {
      api.get("/corto/classifiche-tassi").then((r) => setTassi(r.data)).catch(() => setTassi(null));
      api.get("/corto/redditivita").then((r) => setDati(r.data))
        .catch(() => setErrore("Dati di redditività non disponibili"));
      return;
    }
    api.get("/corto/classifiche").then((r) => setProd(r.data))
      .catch(() => setErrore("Classifiche non disponibili"));
  }, [vista]);

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="classifiche-errore">{errore}</Card>;

  if (vista === "tassi") {
    if (!dati) return <div className="text-sm text-slate-500" data-testid="classifiche-loading">Caricamento…</div>;
    const vuoto = !dati.classifiche.totale.agenzie.length;
    return (
      <div className="space-y-8" data-testid="classifiche-page">
        <SectionTitle sub={`tassi medi, penetrazione assicurativa e redditività · ${dati.meta
          ? `${dati.meta.file} · aggiornato il ${dateIt(dati.meta.aggiornato_il)}`
          : "nessun file di redditività caricato"}`}>
          Classifiche tassi, premi e redditività
        </SectionTitle>

        {tassi ? tassi.ordine.map((campo) => (
          <TabellaTassi key={campo} campo={campo} blocco={tassi.classifiche[campo]}
            mesi={tassi.mesi} />
        )) : null}

        {vuoto ? (
          <Card className="p-8 text-sm text-slate-500" data-testid="classifiche-vuoto">
            Carica il file della redditività da Configurazioni per popolare le classifiche di redditività.
          </Card>
        ) : REDD.map(([tipo, titolo]) => (
          <TabellaRedditivita key={tipo} tipo={tipo} titolo={titolo} mesi={dati.mesi}
            dati={dati.classifiche[tipo]} />
        ))}
      </div>
    );
  }

  const conf = VISTE[vista] || VISTE.caricato;
  if (!prod) return <div className="text-sm text-slate-500" data-testid="classifiche-loading">Caricamento…</div>;

  return (
    <div className="space-y-8" data-testid="classifiche-page">
      <SectionTitle sub={`${conf.sub} · ${prod.anno}`}>{conf.titolo}</SectionTitle>
      {conf.campi.filter((campo) => prod.classifiche[campo]).map((campo) => (
        <TabellaValori key={campo} campo={campo} mesi={prod.mesi}
          blocco={prod.classifiche[campo]} />
      ))}
    </div>
  );
}
